import React, { useState, useEffect } from 'react';
import {
  Box,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Typography,
  Alert
} from '@mui/material';
import { ThumbUp, ThumbDown, Feedback } from '@mui/icons-material';
import { gamoraApi, hasFeedback, getFeedbackKey } from '../services/api';

interface FeedbackButtonsProps {
  generationId: string;
  onFeedbackSubmitted?: () => void;
  showSubmittedMessage?: boolean; // Optional: show a message when feedback is already submitted
}

const FeedbackButtons: React.FC<FeedbackButtonsProps> = ({ 
  generationId, 
  onFeedbackSubmitted,
  showSubmittedMessage = false
}) => {
  const [feedbackDialog, setFeedbackDialog] = useState(false);
  const [feedbackType, setFeedbackType] = useState<'positive' | 'negative' | null>(null);
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);
  const [submittedFeedback, setSubmittedFeedback] = useState<{ score: number; comment?: string } | null>(null);

  // Check localStorage for existing feedback on component mount
  useEffect(() => {
    if (hasFeedback(generationId)) {
      setSubmitted(true);
      // Get the submitted feedback data to show details
      const feedback = localStorage.getItem(getFeedbackKey(generationId));
      if (feedback) {
        try {
          const parsed = JSON.parse(feedback);
          setSubmittedFeedback({ score: parsed.score, comment: parsed.comment });
        } catch (e) {
          // Ignore parsing errors
        }
      }
    }
  }, [generationId]);

  const handleFeedback = async (type: 'positive' | 'negative') => {
    if (submitted) return;
    
    setFeedbackType(type);
    setFeedbackDialog(true);
  };

  const submitFeedback = async () => {
    if (!feedbackType || !generationId) return;

    setSubmitting(true);
    setError(null);

    try {
      const score = feedbackType === 'positive' ? 1 : 0;
      
      await gamoraApi.submitFeedback({
        generation_id: generationId,
        score,
        comment: comment.trim() || undefined
      });

      // Store feedback in localStorage to persist across page refreshes
      const feedbackData = {
        score,
        comment: comment.trim() || undefined,
        timestamp: new Date().toISOString()
      };
      localStorage.setItem(getFeedbackKey(generationId), JSON.stringify(feedbackData));

      setSubmitted(true);
      setSubmittedFeedback({ score, comment: comment.trim() || undefined });
      setFeedbackDialog(false);
      setComment('');
      onFeedbackSubmitted?.();
    } catch (err) {
      setError('Failed to submit feedback. Please try again.');
      console.error('Feedback submission error:', err);
    } finally {
      setSubmitting(false);
    }
  };

  const handleCloseDialog = () => {
    if (!submitting) {
      setFeedbackDialog(false);
      setComment('');
      setError(null);
    }
  };

  // If feedback is already submitted, show message or nothing
  if (submitted) {
    if (showSubmittedMessage && submittedFeedback) {
      return (
        <Box sx={{ mt: 1 }}>
          <Typography 
            variant="caption" 
            color="text.secondary"
            sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: 0.5,
              fontStyle: 'italic'
            }}
          >
            {submittedFeedback.score === 1 ? (
              <>
                <ThumbUp fontSize="small" color="success" />
                Feedback submitted - Thank you!
              </>
            ) : (
              <>
                <ThumbDown fontSize="small" color="error" />
                Feedback submitted - We'll work on improving!
              </>
            )}
          </Typography>
        </Box>
      );
    }
    return null;
  }

  return (
    <>
      <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
        <Tooltip title="This response was helpful">
          <IconButton
            size="small"
            onClick={() => handleFeedback('positive')}
            sx={{
              color: 'action.active',
              '&:hover': { color: 'success.main' }
            }}
          >
            <ThumbUp fontSize="small" />
          </IconButton>
        </Tooltip>
        
        <Tooltip title="This response was not helpful">
          <IconButton
            size="small"
            onClick={() => handleFeedback('negative')}
            sx={{
              color: 'action.active',
              '&:hover': { color: 'error.main' }
            }}
          >
            <ThumbDown fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      <Dialog 
        open={feedbackDialog} 
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Feedback color="primary" />
            Provide Feedback
          </Box>
        </DialogTitle>
        
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            {feedbackType === 'positive' 
              ? 'What did you like about this response?' 
              : 'How can we improve this response?'}
          </Typography>
          
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          
          <TextField
            fullWidth
            multiline
            rows={3}
            variant="outlined"
            placeholder="Share your thoughts (optional)..."
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            disabled={submitting}
          />
        </DialogContent>
        
        <DialogActions>
          <Button 
            onClick={handleCloseDialog}
            disabled={submitting}
          >
            Cancel
          </Button>
          <Button 
            onClick={submitFeedback}
            variant="contained"
            disabled={submitting}
            startIcon={feedbackType === 'positive' ? <ThumbUp /> : <ThumbDown />}
          >
            Submit Feedback
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default FeedbackButtons; 